// Thin API client. Holds the bearer token, talks JSON, and surfaces clean errors.
import type { Flow, VulpariNode } from "@vulpari/core";

const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";
const TOKEN_KEY = "vulpari.token";

export interface AuthUser {
  id: string;
  email: string;
  defaultLedgerId?: string | null;
}

/** A node as returned by the API (a database row). */
export interface NodeRow {
  id: string;
  type: VulpariNode["type"];
  name: string;
  currency: string;
  positionX: number;
  positionY: number;
  meta: VulpariNode["meta"];
}

/** A flow as returned by the API (a database row). */
export interface FlowRow {
  id: string;
  sourceNodeId: string;
  targetNodeId: string;
  amountType: Flow["amountType"];
  amountValue: number;
  schedule: Flow["schedule"];
  label: string;
}

/** A visual group of nodes. */
export interface Group {
  id: string;
  name: string;
  color: string | null;
  collapsed: boolean;
  nodeIds: string[];
}

export interface GraphResponse {
  nodes: NodeRow[];
  flows: FlowRow[];
  groups: Group[];
}

/** A ledger (named canvas) as returned by the API. */
export interface Ledger {
  id: string;
  name: string;
  currency: string;
  startMonth: string | null;
  createdAt: string;
}

/** Fields accepted when updating a ledger's settings. */
export interface LedgerPatch {
  name?: string;
  currency?: string;
  startMonth?: string | null;
}

/** Fields accepted when creating a node. */
export interface NodeCreate {
  ledgerId: string;
  type: VulpariNode["type"];
  name: string;
  currency: string;
  positionX: number;
  positionY: number;
  meta: VulpariNode["meta"];
}

/** Fields accepted when updating a node. */
export type NodePatch = Partial<{
  name: string;
  currency: string;
  positionX: number;
  positionY: number;
  meta: VulpariNode["meta"];
  groupId: string | null;
}>;

/** Fields accepted when updating a flow. */
export type FlowPatch = Partial<{
  sourceNodeId: string;
  targetNodeId: string;
  amountType: Flow["amountType"];
  amountValue: number;
  schedule: Flow["schedule"];
  label: string;
}>;

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null): void {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
}

/** An error from the API, carrying the HTTP status. */
export class ApiClientError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers = new Headers(options.headers);
  headers.set("Content-Type", "application/json");
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const response = await fetch(`${BASE_URL}${path}`, { ...options, headers });

  if (response.status === 204) {
    return undefined as T;
  }

  const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (!response.ok) {
    const message = typeof body.error === "string" ? body.error : "Request failed.";
    throw new ApiClientError(response.status, message);
  }
  return body as T;
}

export const api = {
  async register(email: string, password: string) {
    const result = await request<{ token: string; user: AuthUser }>("/auth/register", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    setToken(result.token);
    return result.user;
  },

  async login(email: string, password: string) {
    const result = await request<{ token: string; user: AuthUser }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    setToken(result.token);
    return result.user;
  },

  me() {
    return request<{ user: AuthUser }>("/me");
  },

  updateEmail(email: string) {
    return request<{ user: AuthUser }>("/me", {
      method: "PATCH",
      body: JSON.stringify({ email }),
    });
  },

  changePassword(currentPassword: string, newPassword: string) {
    return request<void>("/me/password", {
      method: "POST",
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  },

  setDefaultLedger(ledgerId: string) {
    return request<{ user: AuthUser }>("/me", {
      method: "PATCH",
      body: JSON.stringify({ defaultLedgerId: ledgerId }),
    });
  },

  listLedgers() {
    return request<{ ledgers: Ledger[] }>("/ledgers");
  },

  createLedger(name: string) {
    return request<Ledger>("/ledgers", { method: "POST", body: JSON.stringify({ name }) });
  },

  renameLedger(id: string, name: string) {
    return request<Ledger>(`/ledgers/${id}`, { method: "PATCH", body: JSON.stringify({ name }) });
  },

  updateLedger(id: string, patch: LedgerPatch) {
    return request<Ledger>(`/ledgers/${id}`, { method: "PATCH", body: JSON.stringify(patch) });
  },

  deleteLedger(id: string) {
    return request<void>(`/ledgers/${id}`, { method: "DELETE" });
  },

  graph(ledgerId: string) {
    return request<GraphResponse>(`/ledgers/${ledgerId}/graph`);
  },

  seed(ledgerId: string) {
    return request<{ seeded: number }>(`/ledgers/${ledgerId}/seed`, { method: "POST" });
  },

  updateNodePosition(id: string, positionX: number, positionY: number) {
    return request<NodeRow>(`/nodes/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ positionX, positionY }),
    });
  },

  updateNode(id: string, patch: NodePatch) {
    return request<NodeRow>(`/nodes/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  },

  createNode(input: NodeCreate) {
    return request<NodeRow>("/nodes", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  deleteNode(id: string) {
    return request<void>(`/nodes/${id}`, { method: "DELETE" });
  },

  createFlow(input: Omit<FlowRow, "id">) {
    return request<FlowRow>("/flows", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  updateFlow(id: string, patch: FlowPatch) {
    return request<FlowRow>(`/flows/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  },

  deleteFlow(id: string) {
    return request<void>(`/flows/${id}`, { method: "DELETE" });
  },

  createGroup(ledgerId: string, name: string, nodeIds: string[], color: string) {
    return request<Group>("/groups", {
      method: "POST",
      body: JSON.stringify({ ledgerId, name, nodeIds, color }),
    });
  },

  updateGroup(id: string, patch: { name?: string; color?: string; collapsed?: boolean }) {
    return request<Group>(`/groups/${id}`, { method: "PATCH", body: JSON.stringify(patch) });
  },

  addToGroup(id: string, nodeIds: string[]) {
    return request<Group>(`/groups/${id}/members`, {
      method: "POST",
      body: JSON.stringify({ nodeIds }),
    });
  },

  deleteGroup(id: string) {
    return request<void>(`/groups/${id}`, { method: "DELETE" });
  },
};
